import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card, Message, Spinner } from '@iwoca/orion';
import clsx from 'clsx';
import { FileUpload } from 'components/Registration/FileUpload/FileUpload';
import { DOCUMENT_UPLOAD_UK } from 'components/Registration/FileUpload/FileUpload.helpers';
import {
  handleSaveDocuments,
  handlePatchOnboardingFilesUploaded,
} from 'components/Registration/IntroducerOnboarding/RegistrationForm/registrationForm.helpers';
import { TFileWithDocType } from 'components/Registration/IntroducerOnboarding/RegistrationForm/registrationForm.types';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PATH_NAMES } from 'utils/routes/pathNames';
import sharedStyles from '../Registration/IntroducerOnboarding/RegistrationForm/RegistrationForm.module.css';
import { useRegistrationDocuments } from './RegistrationDocuments.helpers';
import styles from './RegistrationDocuments.module.css';
import { TRegistrationDocumentsFormValues } from './RegistrationDocuments.types';

export const RegistrationDocuments = () => {
  const navigate = useNavigate();
  const {
    missingDocTypes,
    isLoading,
    introducerId,
    introducerAccountId,
    schema,
  } = useRegistrationDocuments();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [hasUploadedDocs, setHasUploadedDocs] = useState(false);

  // Handling redirection in case someone accesses the page directly by typing in the URL
  useEffect(() => {
    if (!isLoading && missingDocTypes.length === 0) {
      navigate(PATH_NAMES.home());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, missingDocTypes]);

  const methods = useForm<TRegistrationDocumentsFormValues>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values: TRegistrationDocumentsFormValues) => {
    const selectedFiles: TFileWithDocType[] = [];

    if (values.certifiedIdFile) selectedFiles.push(values.certifiedIdFile);
    if (values.bankStatementFile) selectedFiles.push(values.bankStatementFile);
    if (values.proofOfAddressFile)
      selectedFiles.push(values.proofOfAddressFile);

    if (introducerId) {
      setHasUploadError(false);
      setIsSubmitting(true);
      try {
        // when re-submitting form, if docs have already been uploaded successfully, don't try again - skip to retry the patch
        if (!hasUploadedDocs) {
          await handleSaveDocuments({
            selectedFiles,
            introducerId,
          });
        }
        // await success of the handleSaveDocuments and mark that the documents have been uploaded
        setHasUploadedDocs(true);

        if (introducerAccountId) {
          await handlePatchOnboardingFilesUploaded({ introducerAccountId });
        }
        navigate(PATH_NAMES.home());
      } catch {
        setHasUploadError(true);
      }
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Spinner className={styles.loader} />;
  }

  return (
    <div className={styles.pageContent}>
      <h2 className={styles.heading}>
        Your account's ready - just one more step
      </h2>
      <p className={styles.subHeading}>
        Your account has been created, but there was an issue uploading your
        documents. Please re-upload them below to continue.
      </p>

      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={methods.handleSubmit(onSubmit)}>
          <Card className={styles.card}>
            {missingDocTypes.map((docType) => {
              const docTypeToFormField = {
                bank_statement: 'bankStatementFile',
                certified_id: 'certifiedIdFile',
                proof_of_address: 'proofOfAddressFile',
              } as const;

              const formValue = docTypeToFormField[docType];

              return (
                <FileUpload
                  fileDropClassName={styles.fileDrop}
                  cardClassName={styles.fileDropCard}
                  key={docType}
                  docType={docType}
                  label={DOCUMENT_UPLOAD_UK[docType].label}
                  instruction={DOCUMENT_UPLOAD_UK[docType].instruction}
                  selectedFile={methods.watch(formValue)}
                  errorMessage={methods.formState.errors?.[formValue]?.message}
                  onChange={(file) => methods.setValue(formValue, file)}
                />
              );
            })}
          </Card>
          <Button
            variant="primary"
            size="small"
            type="submit"
            loading={isSubmitting}
            className={clsx(sharedStyles.submitButton, styles.submit)}
          >
            Submit documents
          </Button>
          {hasUploadError && (
            <Message
              className={styles.error}
              type="error"
              message="Something went wrong, please refresh the page and try again."
            />
          )}
        </form>
      </FormProvider>
    </div>
  );
};
